import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Crown, Star, ExternalLink } from "lucide-react";

interface ToolDetail {
  name: string;
  logo: string;
  url: string;
  description: string;
  pricingBreakdown: string[];
  features: string[];
  pros: string[];
  cons: string[];
  isRecommended?: boolean;
}

const toolDetails: ToolDetail[] = [
  {
    name: "Lottae",
    logo: "🏆",
    url: "https://www.lottae.com/pricing",
    description: "A modern project management platform built for teams that want full customization without enterprise pricing.",
    pricingBreakdown: [
      "$7/month base platform fee per workspace",
      "$3.50 per user (minimum 5 users)",
      "10-user team: ~$51/month",
      "25-user team: ~$110/month",
      "60-user team: ~$242/month",
    ],
    features: [
      "Kanban boards & Timeline views",
      "Custom workflows & automations",
      "White-labeling & custom domain",
      "Role-based permissions",
      "Dashboards & reporting",
      "AWS hosted with daily backups",
    ],
    pros: [
      "Lowest per-user pricing in the market",
      "No feature gating — everyone gets everything",
      "White-label included at base price",
      "Highly customizable workflows",
    ],
    cons: [
      "Smaller integration library than Jira",
      "Newer platform (less brand recognition)",
    ],
    isRecommended: true,
  },
  {
    name: "Asana",
    logo: "📋",
    url: "https://asana.com",
    description: "Popular project management tool with strong task management and team collaboration features.",
    pricingBreakdown: [
      "Free: Up to 10 users, basic features",
      "Premium: $10.99/user/month",
      "Business: $24.99/user/month",
      "Enterprise: Custom pricing",
    ],
    features: [
      "Task management & projects",
      "Timeline & workload views",
      "Goals & portfolios",
      "Forms & automations",
    ],
    pros: [
      "Intuitive interface",
      "Strong integrations ecosystem",
      "Good for marketing workflows",
    ],
    cons: [
      "Gets expensive at scale",
      "Limited customization on lower tiers",
      "White-labeling only on Enterprise",
    ],
  },
  {
    name: "Monday.com",
    logo: "📊",
    url: "https://monday.com",
    description: "Visual work OS with colorful boards and strong automation capabilities.",
    pricingBreakdown: [
      "Free: Up to 2 users",
      "Basic: $9/user/month",
      "Standard: $12/user/month",
      "Pro: $19/user/month",
      "Enterprise: Custom pricing",
    ],
    features: [
      "Colorful visual boards",
      "200+ templates",
      "Automations & integrations",
      "Dashboards & charts",
    ],
    pros: [
      "Highly visual interface",
      "Extensive template library",
      "Good automation features",
    ],
    cons: [
      "Pricing adds up quickly",
      "Can be overwhelming for simple needs",
      "Storage limits on lower plans",
    ],
  },
  {
    name: "Jira",
    logo: "🔧",
    url: "https://www.atlassian.com/software/jira",
    description: "Industry-standard for software development teams with powerful agile features.",
    pricingBreakdown: [
      "Free: Up to 10 users",
      "Standard: $7.75/user/month",
      "Premium: $15.25/user/month",
      "Enterprise: Custom pricing",
    ],
    features: [
      "Scrum & Kanban boards",
      "Roadmaps & sprints",
      "1000+ integrations",
      "Advanced reporting",
    ],
    pros: [
      "Best for development teams",
      "Massive integration ecosystem",
      "Powerful query language (JQL)",
    ],
    cons: [
      "Steep learning curve",
      "Overkill for non-dev teams",
      "Can be slow with large projects",
    ],
  },
];

export const PMToolsDetails = () => {
  return (
    <section className="py-8">
      <div className="space-y-4 mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
          Detailed Tool Breakdown
        </h2>
        <p className="text-muted-foreground">
          In-depth look at pricing, features, and use cases for each platform
        </p>
      </div>

      <div className="grid gap-6">
        {toolDetails.map((tool) => (
          <Card 
            key={tool.name}
            className={tool.isRecommended 
              ? "border-2 border-primary bg-gradient-to-br from-primary/5 to-transparent" 
              : ""
            }
          >
            <CardHeader>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-2xl">{tool.logo}</span>
                {tool.isRecommended && (
                  <Crown className="w-6 h-6 text-yellow-500" />
                )}
                <a 
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  <CardTitle className="text-xl sm:text-2xl inline-flex items-center gap-2">
                    {tool.name}
                    <ExternalLink className="w-4 h-4 opacity-50" />
                  </CardTitle>
                </a>
                {tool.isRecommended && (
                  <>
                    <Badge className="bg-primary text-primary-foreground">
                      Best Value
                    </Badge>
                    <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Editor's Choice
                    </Badge>
                  </>
                )}
              </div>
              <p className="text-muted-foreground">{tool.description}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pricing breakdown */}
              <div>
                <h4 className="font-semibold text-foreground mb-3">Pricing Breakdown</h4>
                <ul className="space-y-1.5">
                  {tool.pricingBreakdown.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Features */}
              <div>
                <h4 className="font-semibold text-foreground mb-3">Key Features</h4>
                <div className="flex flex-wrap gap-2">
                  {tool.features.map((feature, i) => (
                    <Badge key={i} variant="secondary" className="font-normal">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Pros & Cons */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Pros
                  </h4>
                  <ul className="space-y-1.5">
                    {tool.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    Cons
                  </h4>
                  <ul className="space-y-1.5">
                    {tool.cons.map((con, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <X className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};
